import React from 'react';
import { getComponentClasses } from '@99designs/frontend-components/utils/app';
import { Role } from '../../consts';
import { Site } from '../../lib/Site';
import LastSeenPixel from '../LastSeenPixel/LastSeenPixel';
import TopLevelNavigationItem from '../TopLevelNav/TopLevelNavigationItem';
import AccountNavigation from './AccountNavigation';
import FocusedNavigation from './FocusedNavigation';
import PrimaryNavigation from './PrimaryNavigation';
import SecondaryNavigation from './SecondaryNavigation';
import SiteHeaderLogo from './SiteHeaderLogo';

interface Props {
    focused?: boolean;
    role: Role;
    site: Site;
    variations: string[];
    onLeftDrawerTriggerClick: () => void;
    onRightDrawerTriggerClick: () => void;
    isStaticTemplate?: boolean;
    isBlog?: boolean;
    hasLogin?: boolean;
}

export default function SiteHeader({
    focused = false,
    role,
    site,
    onLeftDrawerTriggerClick,
    onRightDrawerTriggerClick,
    variations = [],
    isStaticTemplate = false,
    isBlog = false,
    hasLogin = true,
}: Props) {
    const classes = getComponentClasses('site-header', [
        ...variations,
        ...(isStaticTemplate ? ['static'] : []),
        ...(!focused && role !== 'logged-out' ? ['item--branding--center'] : []),
        role === 'logged-out' ? 'signed-out' : 'signed-in',
    ]);

    const languageCode = site.getLanguageCode();

    return (
        <header className={classes}>
            <div className="site-header__inner1">
                <div className="site-header__inner2">
                    <div className="site-header__item site-header__item--left-oc-trigger">
                        <button
                            className="site-header__left-oc-trigger"
                            type="button"
                            data-oc-trigger="left"
                            onClick={onLeftDrawerTriggerClick}
                            aria-label={__('Menu')}
                        />
                    </div>
                    <SiteHeaderLogo role={role} languageCode={languageCode} />
                    <PrimaryNavigation
                        role={role}
                        focused={focused}
                        site={site}
                        isStaticTemplate={isStaticTemplate}
                        isBlog={isBlog}
                    />
                    <SecondaryNavigation
                        role={role}
                        focused={focused}
                        site={site}
                        isStaticTemplate={isStaticTemplate}
                    />
                    <FocusedNavigation focused={focused} site={site} />
                    {hasLogin && (
                        <React.Fragment>
                            <AccountNavigation role={role} isStaticTemplate={isStaticTemplate} />
                            <div className="site-header__item site-header__item--right-oc-trigger">
                                <div className="top-level-nav">
                                    {role === 'logged-out' && (
                                        <TopLevelNavigationItem
                                            label={__('Log in')}
                                            url="/login"
                                            additionalClasses={['top-level-nav__item--first']}
                                            linkAdditionalClasses={['SMOKETEST_login_link']}
                                            linkAttributes={{
                                                'data-globalnavui-loginlink':
                                                    'data-globalnavui-loginlink',
                                            }}
                                        />
                                    )}
                                    {role !== 'logged-out' && (
                                        <button
                                            className="top-level-nav__item top-level-nav__item--no-margin top-level-nav__item--oc-trigger"
                                            type="button"
                                            data-oc-trigger="right"
                                            onClick={onRightDrawerTriggerClick}
                                        >
                                            <div className="avatar avatar--extra-small">
                                                <img
                                                    alt=""
                                                    className="avatar__image"
                                                    src="/avatars/current-user/32"
                                                />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                    {role !== 'logged-out' && <LastSeenPixel />}
                </div>
            </div>
        </header>
    );
}
